import { useEffect, useState } from 'react'
import { fetchCustomEndpointOnce } from './customEndpointFetch'

/** One app user who linked themselves to a pet, as returned by /pets/:id/co-owners. */
export interface PetCoOwner {
  id: number
  name: string | null
  email: string | null
}

/**
 * App users linked to a vet-owned pet (ada-core `GET /api/pets/:id/co-owners`,
 * vic/ada-core#419). The server answers only for the pet's owner of record
 * with role `vet`; everyone else gets 403, which is the COMMON case for a
 * portal user. So every non-200 outcome — 403, 404, 400, 401, a network error
 * or a stalled backend — is an empty list: the section hides, no error shows.
 */
export const usePetCoOwners = (petId: number | string | null | undefined, enabled = true) => {
  const [coOwners, setCoOwners] = useState<PetCoOwner[]>([])

  useEffect(() => {
    // Drop the previous pet's list at once (state resets when the id changes).
    setCoOwners([])
    const id = Number.parseInt(String(petId ?? ''), 10)
    if (!enabled || !Number.isInteger(id) || id <= 0) return

    const controller = new AbortController()
    fetchCustomEndpointOnce(`/pets/${id}/co-owners`, controller.signal)
      .then(async (resp) => {
        if (!resp || !resp.ok || controller.signal.aborted) return
        const data = await resp.json()
        if (controller.signal.aborted || !Array.isArray(data)) return
        setCoOwners(
          data
            .filter((c: any) => c && typeof c === 'object' && Number.isInteger(c.id))
            .map((c: any) => ({ id: c.id, name: c.name ?? null, email: c.email ?? null })),
        )
      })
      .catch(() => {
        /* fail soft: leave the list empty */
      })
    return () => controller.abort()
  }, [petId, enabled])

  return coOwners
}
